"use client";

import { useState } from "react";
import type { ComponentProps } from "react";
import type { LucideIcon } from "lucide-react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

/** Card shell shared by the login / register / password pages. */
export function AuthCard({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-line bg-white p-6 shadow-lift sm:p-8">
      <h1 className="font-display text-3xl font-semibold text-ink">{title}</h1>
      <p className="mt-1 text-sm text-ink-soft">{subtitle}</p>
      {children}
    </div>
  );
}

/** Icon input with label, error and (for type="password") a show/hide toggle. */
export function AuthInput({
  id,
  label,
  icon: Icon,
  error,
  type = "text",
  ...rest
}: { id: string; label: string; icon: LucideIcon; error?: string } & Omit<ComponentProps<"input">, "id">) {
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink">
        {label}
      </label>
      <div className="relative">
        <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
        <input
          id={id}
          type={isPassword && show ? "text" : type}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          className={cn(
            "h-11 w-full rounded-[var(--radius-btn)] border bg-white pl-10 text-[15px] outline-none transition-colors focus:border-brand-500",
            isPassword ? "pr-11" : "pr-3",
            error ? "border-[#b3261e]" : "border-line"
          )}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            aria-label={show ? "Hide password" : "Show password"}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-ink-muted hover:text-ink"
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1.5 text-[13px] font-medium text-[#b3261e]">
          {error}
        </p>
      )}
    </div>
  );
}

export function AuthSubmit({ busy, busyLabel, children }: { busy: boolean; busyLabel: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-[var(--radius-btn)] bg-brand-600 text-base font-medium text-white shadow-soft transition-all hover:bg-brand-700 disabled:opacity-70"
    >
      {busy ? busyLabel : children}
    </button>
  );
}
